import { useEffect, useMemo, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { downloadPDF, previewPDF } from '../lib/pdf';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  BarChart3, Download, Calendar, ChevronRight, ChevronDown, RefreshCw, FileText,
  TrendingUp, Users, IndianRupee, CalendarDays, Eye,
} from 'lucide-react';

const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const inr = (n) => n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const fmtMonth = (m) => {
  if (!m) return '';
  const [y, mm] = m.split('-');
  return `${MONTHS_LONG[parseInt(mm, 10) - 1] || mm} ${y}`;
};
const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

async function _stub() {}
// (kept for compat; real download lives in lib/pdf.js)

function MonthCard({ month, monthly, dailies, onDownload, onPreview, onBackfillMonthly, onBackfillDay, busy }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(`${month}-${new Date().getDate().toString().padStart(2, '0')}`);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`month-${month}`}>
      <button onClick={() => setOpen(!open)}
              className="w-full p-5 flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors text-left"
              data-testid={`expand-${month}`}>
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-[#3D4A8E]/10 text-[#3D4A8E] flex items-center justify-center flex-shrink-0">
            <Calendar className="w-5 h-5" strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <div className="font-heading text-lg font-bold text-slate-900">{fmtMonth(month)}</div>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
              <span>{dailies.length} daily report{dailies.length !== 1 ? 's' : ''}</span>
              {monthly && (
                <>
                  <span className="text-slate-300">·</span>
                  <span><b className="text-slate-700">{inr(monthly.summary?.total_net)}</b> net paid</span>
                  <span className="text-slate-300">·</span>
                  <span><b className="text-slate-700">{monthly.summary?.payslip_count || 0}</b> payslips</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {open ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 bg-slate-50/50 p-5 space-y-4" data-testid={`month-content-${month}`}>
          {/* Monthly report card */}
          {monthly ? (
            <div className="bg-gradient-to-br from-[#3D4A8E] to-[#283593] rounded-xl p-5 text-white" data-testid={`monthly-${month}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-75">Consolidated</div>
                  <div className="font-heading text-2xl font-bold mt-1">Monthly Report</div>
                  <div className="text-xs opacity-75 mt-1">Generated {fmtDate(monthly.generated_at)}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                <Button data-testid={`download-monthly-${month}`} variant="secondary" onClick={() => onDownload(monthly.id, `Monthly_Report_${month}.pdf`)}
                        disabled={busy} className="rounded-full bg-white text-[#3D4A8E] hover:bg-white/90">
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                <Button data-testid={`preview-monthly-${month}`} variant="ghost" onClick={() => onPreview(monthly.id)}
                        disabled={busy} className="rounded-full text-white hover:bg-white/15">
                  <Eye className="w-4 h-4 mr-2" /> Preview
                </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <Stat label="Gross" value={inr(monthly.summary?.total_gross)} />
                <Stat label="Net Paid" value={inr(monthly.summary?.total_net)} />
                <Stat label="Avg Attendance" value={`${monthly.summary?.attendance_pct ?? 0}%`} />
                <Stat label="Joiners / Leavers" value={`+${monthly.summary?.joiners ?? 0} / −${monthly.summary?.leavers ?? 0}`} />
              </div>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 border-dashed rounded-xl p-5 flex items-center justify-between gap-3 flex-wrap" data-testid={`no-monthly-${month}`}>
              <div>
                <div className="font-heading font-semibold text-slate-900">No monthly report yet</div>
                <div className="text-xs text-slate-500 mt-1">It will auto-generate at month-end (18:35 UTC on the last IST day) — or generate it now.</div>
              </div>
              <Button onClick={() => onBackfillMonthly(month)} disabled={busy}
                      data-testid={`backfill-monthly-${month}`} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                <RefreshCw className="w-4 h-4 mr-2" /> Generate now
              </Button>
            </div>
          )}

          {/* Daily reports grid */}
          <div>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Daily reports</div>
              <div className="flex items-center gap-2">
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 rounded-lg w-44" data-testid={`backfill-date-${month}`} />
                <Button variant="outline" size="sm" onClick={() => onBackfillDay(date)} disabled={busy} data-testid={`backfill-day-${month}`} className="rounded-full">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Generate
                </Button>
              </div>
            </div>
            {dailies.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm">
                No daily reports yet. Auto-generates at 00:00 IST nightly, or generate manually above.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {dailies.map(d => (
                  <div key={d.id} data-testid={`daily-${d.date}`}
                       className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-sm hover:-translate-y-0.5 transition-all">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-[#3D4A8E]/10 text-[#3D4A8E] flex items-center justify-center flex-shrink-0">
                          <FileText className="w-4 h-4" strokeWidth={1.8} />
                        </div>
                        <div className="min-w-0">
                          <div className="font-heading font-semibold text-slate-900">{fmtDate(d.date)}</div>
                          <div className="text-[11px] text-slate-500 mt-0.5">{d.date}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => onPreview(d.id)}
                                disabled={busy} className="text-slate-600 h-8 px-2" data-testid={`preview-daily-${d.date}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => onDownload(d.id, `Daily_Report_${d.date}.pdf`)}
                                disabled={busy} className="text-[#3D4A8E]" data-testid={`download-daily-${d.date}`}>
                          <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
                        </Button>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <MiniStat icon={Users} label="Active" value={d.summary?.active ?? '—'} />
                      <MiniStat icon={CalendarDays} label="On Leave" value={d.summary?.on_leave ?? 0} />
                      <MiniStat icon={IndianRupee} label="Reimb" value={inr(d.summary?.reimb_amount)} />
                      <MiniStat icon={TrendingUp} label="Payroll" value={inr(d.summary?.payroll_today)} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-bold uppercase tracking-wider opacity-75">{label}</div>
    <div className="font-heading font-bold text-lg mt-0.5 leading-tight">{value}</div>
  </div>
);

const MiniStat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-2 py-1.5">
    <Icon className="w-3 h-3 text-slate-400 flex-shrink-0" />
    <div className="min-w-0">
      <div className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">{label}</div>
      <div className="text-xs font-semibold text-slate-900 truncate">{value}</div>
    </div>
  </div>
);

export default function Reports() {
  const { user } = useAuth();
  const canView = (user?.roles || [user?.role]).some(r => ['admin', 'hr'].includes(r));
  const isAdmin = (user?.roles || [user?.role]).includes('admin');
  const [months, setMonths] = useState([]);
  const [monthlyByMonth, setMonthlyByMonth] = useState({});
  const [dailyByMonth, setDailyByMonth] = useState({});
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newMonth, setNewMonth] = useState(new Date().toISOString().slice(0, 7));

  const load = async () => {
    try {
      const [monthlyAll, monthList] = await Promise.all([
        api.get('/reports?kind=monthly'),
        api.get('/reports/months'),
      ]);
      const allMonths = new Set(monthList.data);
      monthlyAll.data.forEach(r => allMonths.add(r.date));
      const sorted = Array.from(allMonths).sort().reverse();
      setMonths(sorted);
      const mMap = {}; monthlyAll.data.forEach(r => { mMap[r.date] = r; });
      setMonthlyByMonth(mMap);
      // Load dailies per month in parallel
      const dailies = {};
      await Promise.all(sorted.map(async (m) => {
        const r = await api.get(`/reports?kind=daily&month=${m}`);
        dailies[m] = r.data;
      }));
      setDailyByMonth(dailies);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { if (canView) load(); }, [canView]); // eslint-disable-line

  const download = async (id, filename) => {
    setBusy(true);
    await downloadPDF(`/reports/${id}/pdf`, filename);
    setBusy(false);
  };

  const preview = async (id) => {
    setBusy(true);
    await previewPDF(`/reports/${id}/pdf`);
    setBusy(false);
  };

  const backfillDay = async (date) => {
    if (!isAdmin) return toast.error('Admin only');
    if (!date) return;
    setBusy(true);
    try {
      await api.post(`/reports/regenerate/daily?date=${date}`);
      toast.success(`Daily report generated for ${date}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const backfillMonth = async (month) => {
    if (!isAdmin) return toast.error('Admin only');
    setBusy(true);
    try {
      await api.post(`/reports/regenerate/monthly?month=${month}`);
      toast.success(`Monthly report generated for ${fmtMonth(month)}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addMonth = () => {
    if (!newMonth) return;
    setMonths(prev => Array.from(new Set([newMonth, ...prev])).sort().reverse());
    setDailyByMonth(prev => ({ ...prev, [newMonth]: prev[newMonth] || [] }));
    setAdding(false);
  };

  if (!canView) return <div className="text-slate-500">Admin/HR only.</div>;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Insights</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Reports</h1>
          <p className="text-slate-500 mt-1 text-sm">Daily & monthly consolidated reports. Auto-generated each night at <b>00:00 IST</b>. Click any month to expand.</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setAdding(!adding)} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]" data-testid="add-month-btn">
            <Calendar className="w-4 h-4 mr-2" /> {adding ? 'Cancel' : 'Generate for a month'}
          </Button>
        )}
      </div>

      {adding && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-3 flex-wrap" data-testid="add-month-panel">
          <Input type="month" value={newMonth} onChange={(e) => setNewMonth(e.target.value)} className="rounded-xl w-44" data-testid="new-month-input" />
          <Button onClick={() => { backfillMonth(newMonth); addMonth(); }} disabled={busy} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]" data-testid="generate-new-month">
            <RefreshCw className="w-4 h-4 mr-2" /> Generate monthly
          </Button>
        </div>
      )}

      {/* Banner */}
      <div className="bg-gradient-to-br from-slate-900 to-[#283593] text-white rounded-2xl p-6 flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center"><BarChart3 className="w-5 h-5" /></div>
        <div>
          <div className="font-heading text-lg font-semibold">Auto-generated nightly</div>
          <p className="text-sm text-white/80 mt-1">Daily snapshots run at 00:00 IST · Monthly consolidation runs at the end of every IST month. PDFs are downloadable any time.</p>
        </div>
      </div>

      {months.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400">
          No reports yet. {isAdmin && 'Use "Generate for a month" above to create one.'}
        </div>
      ) : (
        <div className="space-y-3">
          {months.map(m => (
            <MonthCard key={m} month={m}
                       monthly={monthlyByMonth[m]}
                       dailies={dailyByMonth[m] || []}
                       onDownload={download}
                       onPreview={preview}
                       onBackfillMonthly={backfillMonth}
                       onBackfillDay={backfillDay}
                       busy={busy} />
          ))}
        </div>
      )}
    </div>
  );
}
